import { FunctionComponent, useEffect, useState } from 'react';
import styled from 'styled-components';
import { classnames } from '../../utils/classnames';
import { mediaScreen } from '../../utils/mediaScreen';
import { Maybe, Link } from '../../types/contentstack';
import NextLink from 'next/link';
import { Icon } from '../ui/icon';

export const Accordion: FunctionComponent<{
  items?: AccordionItem[];
  invert?: boolean;
}> = ({ items, invert }) => {
  const [openItem, setOpenItem] = useState(items?.[0]?.title || '');

  useEffect(() => {
    setOpenItem(items?.[0]?.title || '');
  }, [items?.[0]?.title]);

  return (
    <Container className="accordion">
      {items?.map((item) => {
        const isOpen = openItem === item.title;

        return (
          <Item
            key={item.title}
            onClick={() => setOpenItem(item.title)}
            className={classnames({
              item: true,
              open: isOpen,
              invert: !!invert,
            })}
          >
            <div className="header">
              {!!item?.icon && <img className="icon" src={item.icon} alt="" />}

              <p className="title">{item.title}</p>
            </div>

            {isOpen && (
              <>
                <div
                  className="content fadeIn"
                  dangerouslySetInnerHTML={{ __html: item.content }}
                />

                {!!item?.links?.length && (
                  <div className="links fadeIn">
                    {item.links.map((link, index) => (
                      <span key={index} className="link">
                        <NextLink href={link?.href!} legacyBehavior passHref>
                          <a href={link?.href!}>{link?.title}</a>
                        </NextLink>

                        <Icon
                          name="external-link"
                          size={16}
                          style={{
                            position: 'relative',
                            left: '3px',
                            top: '-1px',
                          }}
                        />
                      </span>
                    ))}
                  </div>
                )}
              </>
            )}
          </Item>
        );
      })}
    </Container>
  );
};

const Container = styled.div`
  overflow: hidden;
  border-radius: 8px;
`;

const Item = styled.div`
  border-bottom: 1px solid #43557091;
  padding: 16px 24px;
  cursor: pointer;

  &:first-of-type {
    border-top: 1px solid #43557091;
  }

  &:last-of-type {
    &.open {
      border-bottom: 0;
    }
  }

  .header {
    display: flex;
    align-items: center;
    position: relative;

    .icon {
      margin-right: 16px;
      width: 24px;
    }

    .title {
      display: flex;
      font-size: 21px;
      line-height: 28px;
      color: white;
      font-weight: 400;
      flex: 1;
      margin: 0;
      padding-right: 50px;
    }

    &:after {
      content: '';
      position: absolute;
      right: 0px;
      top: 50%;
      width: 28px;
      height: 28px;
      transform: translateY(-50%) rotate(0deg);
      transition: transform 1s ease;
      background: ${(p) => p.theme.colors.teal};
      mask: url("data:image/svg+xml,%3Csvg viewBox='0 0 24 24' stroke='white' stroke-width='2' fill='none' stroke-linecap='round' stroke-linejoin='round' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")
        center/28px 28px no-repeat;
    }
  }

  &.open {
    background-color: #51c0ac0f;
    background-image: url('/img/accordion-gradient.svg');
    background-repeat: no-repeat;
    background-position: top left;
    background-size: cover;
    border-top: 0;

    .header:after {
      transform: translateY(-50%) rotate(180deg);
    }

    .title {
      font-weight: bold;
    }
  }

  .content {
    animation-duration: 1s;
    color: rgba(255, 255, 255, 0.8);
    font-size: 16px;
    line-height: 1.5;
    margin-top: 20px;

    li {
      margin: 15px 0;
    }

    table {
      margin-top: 30px;
    }

    table,
    td,
    th {
      border-collapse: collapse;
      font-size: 15px;
      border: 1px solid rgba(255, 255, 255, 0.3);
    }

    td,
    th {
      padding: 10px 15px;
    }

    th {
      text-transform: uppercase;
    }

    a {
      color: ${(p) => p.theme.colors.teal};
      &:hover {
        text-decoration: underline;
      }
    }
  }

  .links {
    animation-duration: 1s;
    margin-top: 20px;
    display: flex;
    gap: 20px;
    font-size: 14px;

    .link {
      display: flex;
      align-items: center;
      gap: 6px;

      .icon,
      a {
        color: ${(p) => p.theme.colors.teal};
      }

      a {
        font-weight: 500;
        &:hover {
          text-decoration: underline;
        }
      }
    }
  }

  &:hover {
    background-color: #51c0ac0f;
  }

  &.invert {
    border-color: #c9c4f169;

    .title,
    .content {
      color: ${(p) => p.theme.colors.slate1};
    }
  }

  ${mediaScreen('xs', 'md')} {
    .header .title {
      font-size: 16px;
    }

    .content,
    .links {
      font-size: 15px;

      table,
      td,
      th {
        font-size: 13px;
      }

      .icon {
        width: 14px;
        height: 14px;
      }
    }
  }
`;

interface AccordionItem {
  icon?: string;
  title: string;
  content: string;
  links?: Maybe<Array<Maybe<Link>>>;
}
